import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/TagsService';
import { Tag } from '../types';

export const useTag = (tagId?: string) => {
    const queryClient = useQueryClient();

    const tagQuery = useQuery({
        queryKey: ['tag', tagId],
        queryFn: () => {
            if (!tagId) throw new Error('tagId is required');
            return fetch(tagId);
        },
        enabled: !!tagId
    });

    const createMutation = useMutation({
        mutationFn: (values: Tag) => {
            const { created, updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tags'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (record: Tag) => {
            if (!tagId) throw new Error('tagId is required');
            return update(tagId, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tags'] });
            queryClient.invalidateQueries({ queryKey: ['tag', tagId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!tagId) throw new Error('tagId is required');
            const record = tagQuery.data;
            if (!record || !record.name) throw new Error('record with name is required');
            return deleteRecord(tagId, record.name);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tags'] });
        }
    });

    const fetchRecord = () => {
        tagQuery.refetch();
    };

    const createRecord = (values: Tag) => {
        createMutation.mutate(values);
    };

    const updateRecord = (values: Tag) => {
        updateMutation.mutate(values);
    };

    const deleteRecordAction = () => {
        deleteMutation.mutate();
    };

    return {
        record: tagQuery.data,
        loading: tagQuery.isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord: deleteRecordAction
    };
};
